import React from 'react';
import { TextField } from '../../../../components/TextField';
import type { FilterComponentProps } from '../../../jobs/filterComponents/types';
import { accessibilityLabel } from '../../../jobs/filterComponents/utils';

/**
 * A filter input that supports multiple search terms separated by commas or newlines.
 * Prefix a term with ! to exclude matching rows.
 *
 * Examples:
 *   "alpha, beta"     → show rows containing alpha OR beta
 *   "!alpha"          → hide rows containing alpha
 *   "alpha, !beta"    → show rows containing alpha, but hide any containing beta
 */
export const MultiTextFilter: React.FC<FilterComponentProps> = ({ columnDef, onFilterChanged }) => {
  const { tableData } = columnDef;

  if (!tableData) {
    return null;
  }

  // Controlled by material-table's own filter state so a remount of this input (e.g., triggered
  // by a sibling state change on the table) doesn't drop the user's typed text.
  const value = typeof tableData.filterValue === 'string' ? tableData.filterValue : '';

  return (
    <TextField
      inputElementProps={{ 'aria-label': accessibilityLabel(columnDef) }}
      onChange={(event) => onFilterChanged(tableData.id, event.target.value)}
      placeholder="a, b — !c to exclude"
      type="search"
      value={value}
      variant="standard"
    />
  );
};

/**
 * Custom filter function for multi-term matching with exclude support.
 *
 * Terms prefixed with ! are exclusions — if the cell matches ANY exclude term, the row is hidden.
 * Remaining (include) terms — the cell must match at least one include term to show.
 * If only exclude terms are provided, all non-excluded rows are shown.
 */
export function multiTextFilterSearch(search: unknown, cellValue: string | undefined): boolean {
  if (typeof search !== 'string' || search.trim().length === 0) {
    return true;
  }

  const rawTerms = search
    .split(/[,\n]+/)
    .map((t) => t.trim())
    .filter((t) => t.length > 0);

  if (rawTerms.length === 0) {
    return true;
  }

  const includeTerms: string[] = [];
  const excludeTerms: string[] = [];

  rawTerms.forEach((t) => {
    if (t === '!') {
      return; // skip bare !
    }
    if (t.startsWith('!')) {
      excludeTerms.push(t.slice(1).toLowerCase());
    } else {
      includeTerms.push(t.toLowerCase());
    }
  });

  const value = (cellValue ?? '').toLowerCase();

  // Exclusions take priority — if any exclude term matches, hide the row
  if (excludeTerms.some((term) => value.includes(term))) {
    return false;
  }

  // If there are include terms, at least one must match
  if (includeTerms.length > 0) {
    return includeTerms.some((term) => value.includes(term));
  }

  // Only exclude terms were provided and none matched — show the row
  return true;
}
